import { useMemo, useCallback, useState, useEffect } from 'react';

import {
  formatValue,
  useGetDashboardLocationsQuery,
  useGetWarehouseQuery,
  type DashboardOptions
} from '@/entities/dashboard';

import { useProjectSiteParams, useUrlState } from '@/shared/lib';
import type { DateRangeValue } from '@/shared/ui';

import { StorageUrlState } from '../model/types';

const DEFAULT_URL_STATE: StorageUrlState = {
  tab: 'movements',
  metric: 'W',
  group: 'D',
  period: 'L6M',
  fromDate: '',
  toDate: ''
};

export const useStorageOverview = () => {
  const { projectId, siteId } = useProjectSiteParams();
  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_URL_STATE);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  const warehouseParams: DashboardOptions = useMemo(
    () => ({
      ...urlState,
      ...(selectedLocations.length > 0 ? { locIds: selectedLocations } : {}),
      projectId,
      siteId
    }),
    [urlState, selectedLocations, projectId, siteId]
  );

  const { data, isLoading, isFetching, error, refetch } = useGetWarehouseQuery(warehouseParams);
  const { data: locationsData, isLoading: locationsLoading } = useGetDashboardLocationsQuery(
    { projectId },
    { skip: !projectId }
  );

  const updateOption = useCallback(
    (key: keyof DashboardOptions | 'tab', value: string) => {
      setUrlState({ [key]: value });
    },
    [setUrlState]
  );

  const handleDateRangeChange = useCallback(
    (dateRange: DateRangeValue) => {
      setUrlState(dateRange as StorageUrlState);
    },
    [setUrlState]
  );

  const formattedStats = useMemo(() => {
    if (!data?.stats) return null;

    return {
      totalVolume: formatValue(data.stats.totalVolume, urlState.metric),
      totalReceived: formatValue(data.stats.totalReceived, urlState.metric),
      longStandingPerc: `${data.stats.longStandingPerc.toFixed(1)}%`,
      expiringSOH: formatValue(data.stats.expiringSOH, urlState.metric),
      lossTotal: formatValue(data.stats.lossTotal, urlState.metric),
      damageTotal: formatValue(data.stats.damageTotal, urlState.metric)
    };
  }, [data?.stats, urlState.metric]);

  const handleResetFilters = useCallback(() => {
    resetUrlState();
    setSelectedLocations([]);
  }, [resetUrlState]);

  useEffect(() => {
    setSelectedLocations([]);
  }, [projectId, siteId]);

  return {
    // Data
    data,
    formattedStats,
    locationsData,

    // Loading states
    isLoading,
    isFetching,
    locationsLoading,
    error,

    // Filters
    urlState,
    selectedLocations,

    // Project/Site
    projectId,
    siteId,

    // Handlers
    updateOption,
    handleDateRangeChange,
    setSelectedLocations,
    refetch,
    resetFilters: handleResetFilters
  };
};
